import { useCallback, useRef } from 'react';
import { useSlotScreenGeneratorStore } from '../store/slotScreenGeneratorStore';
import { useSlotAssetGeneratorStore } from '../store/slotAssetGeneratorStore';
import { generateScreenImages, saveScreenResultImage } from '../services/slot-screen-generator';
import { MODEL_PROVIDER_KEY_MAP } from '../../core/types/image-generation.types';
import type { ImageModelId } from '../../core/types/image-generation.types';
import { resolveApiKey } from '../utils/api-keys';
import { shouldUseProxy } from '../services/ai-proxy';

export function useSlotScreenGenerator() {
  const abortRef = useRef<AbortController | null>(null);

  const isGenerating = useSlotScreenGeneratorStore(
    (s) => s.screens[s.activeScreenId]?.status === 'generating',
  );

  const hasPrompt = useSlotScreenGeneratorStore(
    (s) => !!(s.screens[s.activeScreenId]?.userPrompt.trim()),
  );

  /**
   * Start generating images for the active screen.
   * Validates the API key, then streams results into the store progressively.
   */
  const generate = useCallback(async () => {
    const store = useSlotScreenGeneratorStore.getState();
    const { activeScreenId, screens, systemPromptJson, generationSettings } = store;
    const screen = screens[activeScreenId];

    if (!screen || !screen.userPrompt.trim()) {
      store.addToast({ id: Date.now().toString(), type: 'info', message: 'Enter a prompt to generate images.', timestamp: Date.now() });
      return;
    }

    // Validate API key before starting
    // Skip validation for Google when backend proxy is available
    const model = (screen.generationModel ?? generationSettings.model) as ImageModelId;
    const requiredProvider = MODEL_PROVIDER_KEY_MAP[model] ?? null;
    if (requiredProvider !== null && !(requiredProvider === 'google' && shouldUseProxy())) {
      const assetStore = useSlotAssetGeneratorStore.getState();
      const storeKey = assetStore.apiKeys[requiredProvider] ?? '';
      const key = resolveApiKey(requiredProvider, storeKey);
      if (!key) {
        const msg = `API key required for ${model}. Configure your ${requiredProvider} API key in the Slot Asset Generator settings.`;
        store.setScreenGenerationError(activeScreenId, msg);
        store.addToast({ id: Date.now().toString(), type: 'error', message: msg, timestamp: Date.now() });
        return;
      }
    }

    // Abort any existing generation
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    store.startScreenGeneration(activeScreenId);

    try {
      for await (const { result } of generateScreenImages(
        systemPromptJson,
        screen,
        {
          model: screen.generationModel ?? generationSettings.model,
          imageCount: screen.generationImageCount ?? generationSettings.imageCount,
        },
        controller.signal,
      )) {
        // Re-read store each iteration to get fresh state (avoids stale closure)
        useSlotScreenGeneratorStore.getState().addScreenResult(activeScreenId, result);

        // Save to project folder (fire-and-forget)
        if (result.status === 'ready' && result.imageUrl) {
          saveScreenResultImage(activeScreenId, result)
            .then((savedPath) => {
              if (savedPath) {
                useSlotScreenGeneratorStore.getState().updateResultSavedPath(activeScreenId, result.id, savedPath);
              }
            })
            .catch(() => {}); // No project root or save failed — still works in-memory
        }
      }
      useSlotScreenGeneratorStore.getState().finishScreenGeneration(activeScreenId);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Cancellation — finishScreenGeneration was already called by cancel()
        return;
      }
      const errMsg = err instanceof Error ? err.message : String(err);
      const s = useSlotScreenGeneratorStore.getState();
      s.setScreenGenerationError(activeScreenId, errMsg);
      s.addToast({ id: Date.now().toString(), type: 'error', message: errMsg, timestamp: Date.now() });
    }
  }, []);

  /**
   * Cancel an in-progress generation.
   */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    const store = useSlotScreenGeneratorStore.getState();
    store.finishScreenGeneration(store.activeScreenId);
  }, []);

  return { generate, cancel, isGenerating, hasPrompt };
}
